import clsx from 'clsx';
import React, { useRef, Fragment, useState, useEffect } from 'react';

import {
  IconSelector,
  IconChevronUp,
  IconChevronDown,
} from '@tabler/icons-react';

import { useFormatText } from '@/hooks/useFormatText';

import Button from '../Button/Button';
import { Checkbox } from '../Checkbox';
import EmptyState from '../EmptyState';
import Pagination from '../Pagination';
import SkeletonLoader from '../SkeletonLoader';

const NOT_FOUND_IMG = '/not-found.svg';

const PRIMARY_ICON_CLASS = 'ms-2.5 text-primary-600';

// ── Types ──────────────────────────────────────────────────────────────────

export interface IAccordionColumn {
  key: string;
  label: string;
  width: number;
  sorter?: boolean;
  /** Key sent to the API for sorting. Falls back to `key` when omitted. */
  sortKey?: string;
}

export interface IAccordionParentRow {
  label: React.ReactNode;
  childCount: number;
}

export interface IAccordionChildRow {
  id: string;
  cells: Record<string, React.ReactNode>;
  label: React.ReactNode;
}

export interface IAccordionTableProps {
  columns: IAccordionColumn[];
  expandColWidth?: number;

  parents: IAccordionParentRow[];
  childrenByParent: IAccordionChildRow[][];

  expandedRows: Set<number>;
  onToggleExpand: (index: number) => void;

  selectedKeys: Set<string>;
  onToggleParent: (index: number, checked: boolean) => void;
  onToggleChild: (childId: string, checked: boolean) => void;
  isParentFullySelected: (index: number) => boolean;
  isParentPartiallySelected: (index: number) => boolean;

  sortField?: string;
  sortDirection?: '' | 'ASC' | 'DESC';
  onSort?: (key: string) => void;

  total: number;
  currentPage: number;
  pageSize: number;
  setCurrentPage: React.Dispatch<React.SetStateAction<number>>;

  isLoading?: boolean;
  isEmptySearch?: boolean;

  emptyImgSrc?: string;
  emptyTitle?: string;
  emptyDescription?: string;
  emptyClassName?: string;
  emptyButtonText?: string;
  emptyButtonIcon?: React.ReactNode;
  onEmptyBtnClick?: () => void;
}

// ── Component ──────────────────────────────────────────────────────────────

const AccordionTable: React.FC<IAccordionTableProps> = ({
  columns,
  expandColWidth = 40,
  parents,
  childrenByParent,
  expandedRows,
  onToggleExpand,
  selectedKeys,
  onToggleParent,
  onToggleChild,
  isParentFullySelected,
  isParentPartiallySelected,
  sortField = '',
  sortDirection = '',
  onSort,
  total,
  currentPage,
  pageSize,
  setCurrentPage,
  isLoading = false,
  isEmptySearch = false,
  emptyImgSrc,
  emptyTitle,
  emptyDescription,
  emptyClassName,
  emptyButtonText,
  emptyButtonIcon,
  onEmptyBtnClick,
}) => {
  const { formatText } = useFormatText();

  // ── Measure expand column width so child card aligns on every screen ───
  const expandThRef = useRef<HTMLTableCellElement>(null);
  const [expandColActualWidth, setExpandColActualWidth] =
    useState(expandColWidth);

  useEffect(() => {
    const th = expandThRef.current;
    if (!th) return;
    const update = () => setExpandColActualWidth(th.offsetWidth);
    update();
    const observer = new ResizeObserver(update);
    observer.observe(th);
    return () => observer.disconnect();
  }, []);

  // ── Sort icon ──────────────────────────────────────────────────────────

  const renderSortIcon = (key: string) => {
    if (sortField !== key) {
      return (
        <IconSelector
          height={20}
          width={20}
          className="ms-2.5 text-neutral-600"
        />
      );
    }
    if (sortDirection === 'ASC') {
      return <IconChevronUp size={16} className={PRIMARY_ICON_CLASS} />;
    }
    if (sortDirection === 'DESC') {
      return <IconChevronDown size={16} className={PRIMARY_ICON_CLASS} />;
    }
    return (
      <IconSelector
        height={20}
        width={20}
        className="ms-2.5 text-neutral-600"
      />
    );
  };

  // ── Parent row ─────────────────────────────────────────────────────────

  const renderParentRow = (parent: IAccordionParentRow, idx: number) => {
    const isExpanded = expandedRows.has(idx);
    const isFullySelected = isParentFullySelected(idx);
    const isPartial = isParentPartiallySelected(idx);

    return (
      <tr
        key={`parent-${idx}`}
        data-testid="accordion-parent-row"
        className={clsx(
          'bg-shades-white! hover:bg-gray-50 transition-colors cursor-pointer!',
          !isExpanded && 'border-b border-neutral-200'
        )}
        onClick={() => onToggleExpand(idx)}
      >
        {/* Expand toggle */}
        <td className="px-0 py-2.75 max-w-0">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onToggleExpand(idx);
            }}
            className="inline-flex items-center justify-center text-neutral-secondary hover:text-neutral-primary transition-colors cursor-pointer w-full"
            aria-label={isExpanded ? 'Collapse' : 'Expand'}
          >
            <IconChevronDown
              size={16}
              className={clsx(
                'transition-transform duration-200',
                isExpanded ? 'rotate-0' : '-rotate-90'
              )}
            />
          </button>
        </td>

        {/* Checkbox + label (first column) */}
        <td className="ps-2 pe-4 py-2.75">
          <div className="flex items-center gap-3">
            <div className="shrink-0" onClick={(e) => e.stopPropagation()}>
              <Checkbox
                id={`parent-${idx}`}
                checked={isFullySelected || isPartial}
                onChange={(checked) => onToggleParent(idx, checked)}
              />
            </div>
            {parent.label}
          </div>
        </td>

        {/* Remaining columns — dashes */}
        {columns.slice(1).map((col) => (
          <td
            key={col.key}
            className="px-4 py-2.75 text-neutral-secondary text-sm"
          >
            -
          </td>
        ))}
      </tr>
    );
  };

  // ── Child row ──────────────────────────────────────────────────────────

  const renderChildRow = (
    child: IAccordionChildRow,
    parentIdx: number,
    isLastRow = false
  ) => {
    const isSelected = selectedKeys.has(child.id);

    return (
      <tr
        key={`child-${parentIdx}-${child.id}`}
        data-testid="accordion-child-row"
        className={clsx(
          'bg-shades-white! hover:bg-gray-50 transition-colors',
          !isLastRow && 'border-b border-neutral-200',
          isSelected && 'bg-brand-light-25!'
        )}
      >
        {/* Checkbox + label */}
        <td className="ps-2 pe-4 py-2.75">
          <div className="flex items-center gap-3">
            <Checkbox
              id={`child-${child.id}`}
              checked={isSelected}
              onChange={(checked) => onToggleChild(child.id, checked)}
            />
            {child.label}
          </div>
        </td>

        {/* Data columns */}
        {columns.slice(1).map((col) => (
          <td
            key={col.key}
            className="px-4 py-2.75 text-sm text-neutral-primary"
          >
            {child.cells[col.key] ?? (
              <span className="text-neutral-secondary">-</span>
            )}
          </td>
        ))}
      </tr>
    );
  };

  // Child group wrapper (card-style layout under parent)

  const renderChildGroup = (
    children: IAccordionChildRow[],
    parentIdx: number
  ) => {
    if (!children.length) return null;

    return (
      <tr
        key={`child-group-${parentIdx}`}
        className="border-b border-neutral-200"
      >
        <td colSpan={columns.length + 1} className="pt-4 pb-6">
          <div
            className="border border-neutral-200 rounded-xl bg-shades-white! shadow-small overflow-hidden me-3"
            style={{ marginInlineStart: expandColActualWidth }}
          >
            <table className="w-full text-sm text-start text-gray-500 border-collapse table-fixed">
              <colgroup>
                {columns.map((col) => (
                  <col key={col.key} style={{ width: col.width }} />
                ))}
              </colgroup>
              <tbody>
                {children.map((child, i) =>
                  renderChildRow(child, parentIdx, i === children.length - 1)
                )}
              </tbody>
            </table>
          </div>
        </td>
      </tr>
    );
  };

  // ── Skeleton rows ──────────────────────────────────────────────────────

  const renderSkeleton = () =>
    Array.from({ length: pageSize }).map((_, i) => (
      <tr
        key={`skel-${i}`}
        className="border-b border-neutral-200 bg-shades-white! animate-pulse"
      >
        <td className="px-0 py-2.75 w-8">
          <SkeletonLoader className="h-6 w-6 mx-auto" variant="rect" />
        </td>
        <td className="px-4 py-2.75">
          <SkeletonLoader className="h-6 w-40" variant="rect" />
        </td>
        {columns.slice(1).map((col) => (
          <td key={col.key} className="px-4 py-2.75">
            <SkeletonLoader className="h-6 w-24" variant="rect" />
          </td>
        ))}
      </tr>
    ));

  // ── Table body content ─────────────────────────────────────────────────

  const renderTableBody = (): React.ReactNode[] => {
    if (isLoading) return renderSkeleton();

    if (parents.length > 0) {
      return parents.map((parent, idx) => {
        const isExpanded = expandedRows.has(idx);
        const children = childrenByParent[idx] ?? [];

        return (
          <Fragment key={`group-${idx}`}>
            {renderParentRow(parent, idx)}
            {isExpanded && renderChildGroup(children, idx)}
          </Fragment>
        );
      });
    }

    return [
      <tr key="empty">
        <td colSpan={columns.length + 1}>
          <EmptyState
            imageSrc={emptyImgSrc || NOT_FOUND_IMG}
            title={
              isEmptySearch
                ? formatText('common.table.no-search-title')
                : emptyTitle || formatText('common.no-data-found')
            }
            description={
              isEmptySearch
                ? formatText('common.table.no-search-subtitle')
                : emptyDescription || ''
            }
            className={clsx(emptyClassName)}
            actions={
              !isEmptySearch && emptyButtonText ? (
                <Button
                  label={emptyButtonText}
                  variant="primary"
                  icon={emptyButtonIcon}
                  className="h-12 px-4 cursor-pointer"
                  onClick={onEmptyBtnClick}
                />
              ) : undefined
            }
          />
        </td>
      </tr>,
    ];
  };

  // ── Main render ────────────────────────────────────────────────────────

  return (
    <div className="relative">
      <div className="flex flex-col border border-neutral-200 rounded-2xl overflow-hidden bg-white relative">
        <div className="overflow-auto custom-scrollbar grow">
          <table className="w-full shadow-small text-sm text-start text-gray-500 border-collapse table-fixed">
            <colgroup>
              <col style={{ width: expandColWidth }} />
              {columns.map((col) => (
                <col key={col.key} style={{ width: col.width }} />
              ))}
            </colgroup>

            {/* Header */}
            <thead className="bg-neutral-100 sticky top-0 z-20">
              <tr>
                <th
                  ref={expandThRef}
                  className="px-0 py-3 bg-neutral-100 w-8 text-start rounded-tl-lg!"
                />
                {columns.map((col, idx) => (
                  <th
                    key={col.key}
                    className={clsx(
                      'py-3 font-medium text-sm text-neutral-secondary bg-neutral-100 text-start whitespace-nowrap overflow-visible',
                      col.width <= 90 ? 'px-2' : 'px-4',
                      col.sorter && onSort
                        ? 'cursor-pointer'
                        : 'cursor-default',
                      idx === columns.length - 1 && 'rounded-tr-lg!'
                    )}
                    onClick={
                      col.sorter && onSort
                        ? () => onSort(col.sortKey ?? col.key)
                        : undefined
                    }
                  >
                    <div className="flex items-center min-w-0">
                      <span className="truncate">{col.label}</span>
                      {col.sorter &&
                        onSort &&
                        renderSortIcon(col.sortKey ?? col.key)}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>

            <tbody>{renderTableBody()}</tbody>
          </table>
        </div>

        {/* Pagination */}
        {!isLoading && total > 0 && (
          <Pagination
            total={total}
            currentPage={currentPage}
            pageSize={pageSize}
            setCurrentPage={setCurrentPage}
            isLoading={isLoading}
          />
        )}
      </div>
    </div>
  );
};

export default AccordionTable;
